import { useState, useEffect } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Project } from '../types/project';
import { useAuth } from './useAuth';

interface UseProjectReturn {
  project: Project | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and subscribe to a single project
 * Validates user ownership and provides real-time updates
 */
export const useProject = (projectId: string | undefined): UseProjectReturn => {
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state if no projectId or user
    if (!projectId || !user) {
      setProject(null);
      setLoading(false);
      return;
    }

    try {
      // Reference to the project document
      const projectRef = doc(db, 'projects', projectId);

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        projectRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            setError(new Error('Project not found'));
            setProject(null);
            setLoading(false);
            return;
          }

          const data = snapshot.data();

          // Validate user has access (owner or collaborator)
          const isOwner = data.ownerId === user.uid;
          const isCollaborator = data.collaborators?.includes(user.uid);

          if (!isOwner && !isCollaborator) {
            setError(new Error('Unauthorized: You do not have access to this project'));
            setProject(null);
            setLoading(false);
            return;
          }

          const projectData: Project = {
            projectId: snapshot.id,
            ownerId: data.ownerId,
            collaborators: data.collaborators || [],
            collaboratorEmails: data.collaboratorEmails || [],
            name: data.name,
            description: data.description,
            status: data.status,
            researchReportUrl: data.researchReportUrl,
            researchReportMd: data.researchReportMd,
            createdAt: data.createdAt instanceof Timestamp
              ? data.createdAt.toDate()
              : new Date(data.createdAt),
            updatedAt: data.updatedAt instanceof Timestamp
              ? data.updatedAt.toDate()
              : new Date(data.updatedAt),
          };

          setProject(projectData);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching project:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch project'));
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up project listener:', err);
      setError(err instanceof Error ? err : new Error('Failed to set up project listener'));
      setLoading(false);
    }
  }, [projectId, user]);

  return { project, loading, error };
};
